import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from 'react-international-phone';
import { useLoaderData } from 'react-router-dom';
import { DraftFunction } from 'use-immer';

import { PatternFormat } from 'react-number-format';

import { PaymentProcessor } from '@r6digital/r6pay-types/types/PaymentProcessor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { CreditCardTypeId } from '@r6digital/rapidstor-types/enums/sitelink';
import {
    detectCardTypeFromPartial,
    luhnCheck,
} from '@r6digital/rapidstor-types/react/entities/CreditCard';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';

import { WizardValidationError } from '@/core/types/Wizard';
import { getDatePickerRange, RapidStorSettings } from '@/core/utils/rapidstor';
import { isSafeUrl } from '@/core/utils/url';
import { StripePaymentIntent } from '@/features/rapidstor/services/R6PayService';

import StripePaymentForm from '@/core/components/wizard/forms/stripe-payment-form';

import Button from '@/core/components/elements/button';
import CardBrandIcon from '@/core/components/elements/card-brand-icon';
import CircleCheckbox from '@/core/components/elements/circle-checkbox';
import ErrorTag from '@/core/components/elements/error-tag';
import Spinner from '@/core/components/elements/spinner';
import { CardExpiry } from '@/core/components/wizard/forms/payment-form';

import useDefaultCountry from '@/core/hooks/useDefaultCountry';

import 'react-international-phone/style.css';

import classes from './styles.module.scss';

interface Props {
    form: Record<string, string>;
    update: (key: string, event: React.BaseSyntheticEvent) => void;
    updateForm: (arg: Record<string, string> | DraftFunction<Record<string, string>>) => void;
    updateNeededDate: (date: Date) => void;
    updateExpiryDate: (values: Record<string, string | number>) => void;
    updateReservation: (doCostRetrieve: boolean, insuranceCoverage?: string) => void;
    validationErrors: WizardValidationError[];
    availableInsuranceCoverage: InsuranceCoverage[] | undefined;
    reservation: BaseReservation | undefined;
    moveInLoading: boolean;
    pricingPending: boolean;
    moveInError: string | undefined;
    setMoveInError: (error: string | undefined) => void;
    processBooking: (payment_method?: PaymentProcessor) => void;
    r6PayService: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        service: any;
    };
    confirmBookingButtonText: (confirmText?: string) => string;
    stripeIntent: StripePaymentIntent | null;
}

export default function SimplifiedCheckoutForm({
    form,
    update,
    updateForm,
    updateNeededDate,
    updateExpiryDate,
    updateReservation,
    validationErrors,
    availableInsuranceCoverage,
    reservation,
    moveInLoading,
    pricingPending,
    moveInError,
    setMoveInError,
    processBooking,
    r6PayService,
    confirmBookingButtonText,
    stripeIntent,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const defaultCountry = useDefaultCountry(location);

    const dateRange = useMemo(
        () => getDatePickerRange(location, 'movein'),
        [location],
    );

    const showInsurance = useMemo(
        () => RapidStorSettings(location)
            .insurance()
            .show(reservation, availableInsuranceCoverage),
        [location, reservation, availableInsuranceCoverage],
    );

    const showPaymentFields = RapidStorSettings(location).paymentForm().show();

    const [cardNumberError, setCardNumberError] = useState<string | null>(null);
    const [expiryError, setExpiryError] = useState<string | null>(null);

    const partialCardType = useMemo(() => detectCardTypeFromPartial(form.cardNumber), [form.cardNumber]);
    const isAmex = partialCardType === CreditCardTypeId.AmericanExpress;
    const cardNumberPattern = isAmex ? '#### ###### #####' : '#### #### #### ####';
    const cvvPlaceholder = isAmex
        ? t('wizard:paymentForm:csvAmexPlaceholder')
        : t('wizard:paymentForm:csvPlaceholder');
    const cvvMaxLength = isAmex ? 4 : 3;

    const handleExpiryBlur = () => {
        const month = form.cardExpiryMonth;
        const year = form.cardExpiryYear;
        if (!month || !year || year.length < 2) {
            setExpiryError(null);
            return;
        }
        const fullYear = 2000 + parseInt(year, 10);
        const firstMomentNextMonth = new Date(fullYear, parseInt(month, 10), 1);
        if (new Date() >= firstMomentNextMonth) {
            setExpiryError(t('wizard:paymentForm:errors:cardExpiryExpired'));
        } else {
            setExpiryError(null);
        }
    };

    const handleCardNumberBlur = () => {
        if (!form.cardNumber) {
            setCardNumberError(null);
            return;
        }
        if (!luhnCheck(form.cardNumber)) {
            setCardNumberError(t('wizard:paymentForm:errors:cardNumberLuhnInvalid'));
        } else {
            setCardNumberError(null);
        }
    };

    const handleConfirmBooking = () => {
        let hasError = false;

        if (form.cardNumber && !luhnCheck(form.cardNumber)) {
            setCardNumberError(t('wizard:paymentForm:errors:cardNumberLuhnInvalid'));
            hasError = true;
        }

        const month = form.cardExpiryMonth;
        const year = form.cardExpiryYear;
        if (month && year && year.length >= 2) {
            const fullYear = 2000 + parseInt(year, 10);
            const firstMomentNextMonth = new Date(fullYear, parseInt(month, 10), 1);
            if (new Date() >= firstMomentNextMonth) {
                setExpiryError(t('wizard:paymentForm:errors:cardExpiryExpired'));
                hasError = true;
            }
        }

        if (hasError) return;
        processBooking();
    };

    const formErrors = useMemo(
        () => validationErrors?.filter(
            (error) => [
                'firstName', 'lastName', 'email', 'phone',
                'neededDate', 'insuranceCoverage',
                'cardHolderName', 'cardNumber', 'cardExpiryMonth', 'cardExpiryYear', 'cardExpiry', 'cardCsv',
                'autobillUsePaymentDetails',
                'termsAndConditionsAgree',
            ].includes(error.name),
        ),
        [validationErrors],
    );

    return (
        <div className={classes.formColumn}>
            <div className={classes.formSection}>
                {formErrors.length > 0 && (
                    <div className={classes.errorList}>
                        {formErrors.map((error, index) => (
                            <ErrorTag key={`${index}_error`} error={error} />
                        ))}
                    </div>
                )}

                <div className={classes.formTitle}>{t('wizard:booking')}</div>

                {/* Move-in date */}
                <div className={classes.field}>
                    <label>{t('wizard:selectDate')}</label>
                    <DatePicker
                        selected={moment(form.neededDate).toDate()}
                        minDate={dateRange?.min}
                        maxDate={dateRange?.max}
                        required
                        onFocus={(e) => {
                            e.currentTarget.blur();
                        }}
                        excludeDates={(location.rapidstor.datesClosed || '').split(',').filter(Boolean).map((d) => moment(d.trim(), 'DD-MM-YYYY').toDate())}
                        disabledKeyboardNavigation
                        dateFormat={location.rapidstor.dateFormat}
                        onChange={(e: Date | null) => {
                            if (e) {
                                updateNeededDate(e);
                            }
                        }}
                        className={classes.datepicker}
                    />
                </div>

                {/* Insurance dropdown */}
                {showInsurance && (
                    <div className={classes.field}>
                        <label>{t('wizard:insuranceCoverage:title')}</label>
                        <select
                            value={form.insuranceCoverage}
                            onChange={(e) => {
                                updateForm((draft) => {
                                    draft.insuranceCoverage = e.target.value;
                                    return draft;
                                });
                                updateReservation(true, e.target.value);
                            }}
                        >
                            <option value="">{t('wizard:insuranceCoverage:noInsuranceSelected')}</option>
                            {availableInsuranceCoverage?.map((coverage) => (
                                <option key={coverage._id} value={coverage._id}>
                                    {coverage.sCoverageDesc}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Name fields */}
                <div className={classes.nameRow}>
                    <div className={`${classes.field} ${classes.nameField}`}>
                        <label>{t('contact_card:first_name')}</label>
                        <input
                            type="text"
                            placeholder={t('contact_card:first_name')}
                            value={form.firstName}
                            onChange={(e) => update('firstName', e)}
                        />
                    </div>
                    <div className={`${classes.field} ${classes.nameField}`}>
                        <label>{t('contact_card:last_name')}</label>
                        <input
                            type="text"
                            placeholder={t('contact_card:last_name')}
                            value={form.lastName}
                            onChange={(e) => update('lastName', e)}
                        />
                    </div>
                </div>

                {/* Email */}
                <div className={classes.field}>
                    <label>{t('contact_card:email')}</label>
                    <input
                        type="email"
                        placeholder={t('contact_card:email')}
                        value={form.email}
                        onChange={(e) => update('email', e)}
                    />
                </div>

                {/* Phone */}
                <div className={classes.field}>
                    <label>{t('contact_card:phone')}</label>
                    {window.rapidstor_settings.rapidstor_options.dialCodeSelector ? (
                        <PhoneInput
                            defaultCountry={defaultCountry}
                            placeholder={t('contact_card:phone')}
                            value={form.phone}
                            onChange={(phone) => updateForm((draft) => {
                                draft.phone = phone;
                            })}
                        />
                    ) : (
                        <input
                            type="tel"
                            placeholder={t('contact_card:phone')}
                            value={form.phone}
                            onChange={(e) => update('phone', e)}
                        />
                    )}
                </div>

                {/* Card fields */}
                {showPaymentFields && (
                    <>
                        <div className={classes.field}>
                            <label>{t('wizard:paymentForm:cardHolderName')}</label>
                            <input
                                type="text"
                                placeholder={t('wizard:paymentForm:cardHolderNamePlaceholder')}
                                value={form.cardHolderName}
                                onChange={(e) => update('cardHolderName', e)}
                            />
                        </div>
                        <div className={classes.field}>
                            <label>{t('wizard:paymentForm:cardNumber')}</label>
                            <div className={classes.cardNumberWrapper}>
                                <PatternFormat
                                    format={cardNumberPattern}
                                    placeholder={t('wizard:paymentForm:cardNumberPlaceholder')}
                                    value={form.cardNumber}
                                    onValueChange={(values) => {
                                        update('cardNumber', { target: { value: values.value } } as React.BaseSyntheticEvent);
                                    }}
                                    onBlur={handleCardNumberBlur}
                                />
                                <CardBrandIcon
                                    typeId={partialCardType}
                                    className={classes.cardBrandIcon}
                                />
                            </div>
                            {cardNumberError && (
                                <span className={classes.fieldError}>{cardNumberError}</span>
                            )}
                        </div>
                        <div className={classes.cardFieldsRow}>
                            <div className={classes.field}>
                                <label>{t('wizard:paymentForm:expiry')}</label>
                                <CardExpiry
                                    required
                                    name="cardExpiry"
                                    onValueChange={(values: Record<string, string | number>) => {
                                        updateExpiryDate(values);
                                    }}
                                    onBlur={handleExpiryBlur}
                                    month={form.cardExpiryMonth}
                                    year={form.cardExpiryYear}
                                />
                                {expiryError && (
                                    <span className={classes.fieldError}>{expiryError}</span>
                                )}
                            </div>
                            <div className={classes.field}>
                                <label>{t('wizard:paymentForm:csv')}</label>
                                <input
                                    type="text"
                                    placeholder={cvvPlaceholder}
                                    maxLength={cvvMaxLength}
                                    value={form.cardCsv}
                                    onChange={(e) => update('cardCsv', e)}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Autobill */}
                <div className={classes.termsSection}>
                    {showPaymentFields && RapidStorSettings(location).autobill().show() && (
                        <CircleCheckbox
                            // Autobill is the only option in simplified; when it's required it
                            // must stay on, so lock the toggle checked.
                            value={
                                RapidStorSettings(location).autobill().required()
                                    ? 'true'
                                    : form.autobillUsePaymentDetails
                            }
                            disabled={RapidStorSettings(location).autobill().required()}
                            update={() => {
                                if (RapidStorSettings(location).autobill().required()) return;
                                updateForm((draft) => {
                                    const enabling = draft.autobillUsePaymentDetails !== 'true';
                                    draft.autobillUsePaymentDetails = enabling ? 'true' : 'false';
                                    draft.autobillType = enabling ? 'cc' : '';
                                });
                            }}
                            label={t('wizard:autobillUsePaymentDetails')}
                            formKey="autobillUsePaymentDetails"
                        />
                    )}
                    {isSafeUrl(location.rapidstor.termsAndConditionsUrl) && (
                        <a
                            href={location.rapidstor.termsAndConditionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.termsLink}
                        >
                            {t('contact_card:terms_and_conditions')}
                        </a>
                    )}
                    <CircleCheckbox
                        value={form.termsAndConditionsAgree}
                        update={updateForm}
                        label={t('wizard:termsAndConditionsAgree')}
                        formKey="termsAndConditionsAgree"
                    />
                </div>

                {/* Error alert */}
                {moveInError && (
                    <div style={{ color: '#d04d52', fontSize: '14px' }}>
                        {moveInError}
                    </div>
                )}

                {stripeIntent ? (
                    <StripePaymentForm
                        intent={stripeIntent}
                        onError={(msg) => setMoveInError(msg)}
                        buttonText="Pay Now"
                    />
                ) : (
                    <div className={classes.submitSection}>
                        {!showPaymentFields ? (
                            r6PayService.service?.paymentMethods?.map(
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                (method: any) => (
                                    <Button
                                        key={method.id}
                                        disableScroll
                                        onclick={() => {
                                            processBooking(method.type);
                                        }}
                                        type="primary"
                                        size="medium"
                                        disabled={moveInLoading || pricingPending}
                                        style={{ marginBottom: '12px' }}
                                    >
                                        {moveInLoading ? (
                                            <Spinner size="25" inverted />
                                        ) : (
                                            <>
                                                {confirmBookingButtonText(
                                                    method.custom_fields?.display_name,
                                                )}
                                            </>
                                        )}
                                    </Button>
                                ),
                            )
                        ) : (
                            <Button
                                disableScroll
                                onclick={handleConfirmBooking}
                                type="primary"
                                size="medium"
                                disabled={moveInLoading || pricingPending}
                            >
                                {moveInLoading ? (
                                    <Spinner size="25" inverted />
                                ) : (
                                    <>{confirmBookingButtonText()}</>
                                )}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
